import {
  useMergedShopFormulas,
  useShopifyMultipass,
  useGetAllPlansQuery,
  FormulaProduct,
} from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import {
  useFindPlanBySkuSet,
  findPlanBySkuSet,
} from '@viome/viome-base/src/usecase/Shop/ShopUtil';
import { useGetSubscriptionStatusQuery } from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import {
  CustomizeModeView,
  AllSubscribedView,
  AnySubscribedView,
  NotSubscribedView,
} from './components/ShopPageViews';
import { useState, useEffect, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useManageSubscriptionLink } from '@/hooks/useManageSubscriptionLink';
import { useIsComponentizationUser } from '@/hooks/useIsComponentizationUser';
import { useShopFormulasRefetchGuard } from '@/hooks/useShopFormulasRefetchGuard';
import { useNavigate } from 'react-router-dom';
import { ShopFormulaSkeleton } from '@screens/ShopPage/components/ShopFormulaSkeleton';
import EventTracker from '@utils/EventTracker';
import { analyticsEvent } from '@utils/analyticsEvent';
import { ShopUnavailablePage } from '@screens/ShopPage/components/ShopUnavailablePage';
import ENV_VALUES from '@/config/environment';
import { getDiscountedPrice, getFormulaPriceInfo } from './utils/pricing';

export interface ShopFormulasPageProps {
  onCustomizeModeChange?: (isCustomizing: boolean) => void;
}

export const ShopFormulasPage = ({
  onCustomizeModeChange,
}: ShopFormulasPageProps) => {
  const navigate = useNavigate();
  const manageSubscriptionLink = useManageSubscriptionLink();
  const {
    isComponentizationUser,
    isMultiFormulaSelectUser,
    isLoading: isLoadingComponentizationStatus,
  } = useIsComponentizationUser();
  const { refetch: refetchSubscriptionStatus } =
    useGetSubscriptionStatusQuery();
  const {
    data: apiData,
    isLoading: isLoadingFormulas,
    isError: isErrorFormulas,
    refetch: refetchFormulas,
  } = useMergedShopFormulas();
  const { isRefetchSuppressed } = useShopFormulasRefetchGuard();

  const products: FormulaProduct[] | undefined = apiData?.products;
  const promo = apiData?.promo;
  const offerDuration = apiData?.offerDuration;

  useEffect(() => {
    // Skip on the rare mount right after a refetch was explicitly suppressed
    // elsewhere in the Shop flow (see useShopFormulasRefetchGuard).
    if (!isRefetchSuppressed()) {
      refetchSubscriptionStatus();
      refetchFormulas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on mount only; refetchFormulas is a new ref every render
  }, []);

  const [isCustomizeMode, setIsCustomizeMode] = useState(false);
  const [productSelection, setProductSelection] = useState<Set<string> | null>(
    null,
  );

  const { data: allPlans } = useGetAllPlansQuery();

  const { mutate: generateMultipassUrl, isPending: isMultipassLoading } =
    useShopifyMultipass();
  const { toast } = useToast();

  useEffect(() => {
    if (onCustomizeModeChange) {
      onCustomizeModeChange(isCustomizeMode);
    }
  }, [isCustomizeMode, onCustomizeModeChange]);

  const { isAnySubscribed, isAllSubscribed, nonSubscribedProducts } =
    useMemo(() => {
      if (!products) {
        return {
          isAnySubscribed: false,
          isAllSubscribed: false,
          nonSubscribedProducts: [],
          subscribedProducts: [],
        };
      }
      const anySubscribed = products.some((p) => p.is_subscribed);
      const allSubscribed = products.every((p) => p.is_subscribed);
      return {
        isAnySubscribed: anySubscribed,
        isAllSubscribed: allSubscribed,
        nonSubscribedProducts: products.filter((p) => !p.is_subscribed),
        subscribedProducts: products.filter((p) => p.is_subscribed),
      };
    }, [products]);

  const allSelectableProductIds = useMemo(
    () =>
      new Set(
        nonSubscribedProducts.map((product) => String(product.product_id)),
      ),
    [nonSubscribedProducts],
  );
  const isSingleSelectFlow =
    isAnySubscribed && isComponentizationUser && !isMultiFormulaSelectUser;
  // Checkbox and bundle flows default to all; radio buyers must choose one.
  const selectedProductIds = useMemo(() => {
    if (productSelection === null) {
      return isSingleSelectFlow ? new Set<string>() : allSelectableProductIds;
    }
    const availableIds = [...productSelection].filter((id) =>
      allSelectableProductIds.has(id),
    );
    return new Set(
      isSingleSelectFlow ? availableIds.slice(0, 1) : availableIds,
    );
  }, [productSelection, allSelectableProductIds, isSingleSelectFlow]);

  // Default pricing object when no price can be determined or
  // when explicitly showing "Select items".
  const unavailablePricing = useMemo(
    () => ({
      displayMonthlyPrice: 'N/A',
      greenDaily: 'N/A',
      strikethroughDaily: null,
      isPriceAvailable: false,
      hasDiscount: false,
      monthlyPriceWithOutDiscount: 'N/A',
      offerDuration: 'N/A',
    }),
    [],
  );

  const currentSelectedSkusArray = useMemo(() => {
    if (!products) return [];
    const skus = new Set<string>();
    products.forEach((p) => {
      if (selectedProductIds.has(String(p.product_id))) {
        skus.add(p.sku);
      }
    });
    return Array.from(skus);
  }, [products, selectedProductIds]);

  const { plan: matchedPlanForSelected } = useFindPlanBySkuSet(
    currentSelectedSkusArray,
  );

  const allNonSubscribedSkusArray = useMemo(() => {
    return nonSubscribedProducts.map((p) => p.sku);
  }, [nonSubscribedProducts]);

  const { plan: planForNonSubscribed } = useFindPlanBySkuSet(
    allNonSubscribedSkusArray,
  );

  const dynamicPricing = useMemo(() => {
    if (isAnySubscribed && !isComponentizationUser) {
      return unavailablePricing;
    }

    // If all formulas are deselected, hide the price instead of showing $0.00
    // or falling back to the "all non-subscribed" bundle price, whether they
    // have an existing subscription or not.
    if (isComponentizationUser && currentSelectedSkusArray.length === 0) {
      return unavailablePricing;
    }

    let planPrice: number | undefined;
    let selectedMonthlyPrice: number | undefined;

    if (currentSelectedSkusArray.length > 0) {
      if (
        isMultiFormulaSelectUser ||
        (isComponentizationUser && currentSelectedSkusArray.length > 1)
      ) {
        // Match the cards: round each formula before summing the selection.
        let originalTotal = 0;
        let monthlyTotal = 0;
        for (const sku of currentSelectedSkusArray) {
          const plan = findPlanBySkuSet([sku], allPlans);
          const price = Number(plan?.plan_product?.price);
          if (
            !plan?.plan_product?.price?.trim() ||
            !Number.isFinite(price) ||
            price < 0
          ) {
            return unavailablePricing;
          }
          originalTotal += price;
          monthlyTotal += getDiscountedPrice(
            price,
            0,
            apiData?.discountPercentage,
          ).discountedPrice;
        }
        planPrice = originalTotal;
        selectedMonthlyPrice = monthlyTotal;
      } else {
        planPrice = matchedPlanForSelected?.plan_product
          ? parseFloat(matchedPlanForSelected.plan_product.price)
          : undefined;
      }
    } else {
      if (isCustomizeMode) {
        return unavailablePricing;
      }
      if (nonSubscribedProducts.length > 0) {
        planPrice = planForNonSubscribed?.plan_product
          ? parseFloat(planForNonSubscribed.plan_product.price)
          : undefined;
      }
    }

    return planPrice === undefined ||
      !Number.isFinite(planPrice) ||
      planPrice < 0
      ? unavailablePricing
      : getFormulaPriceInfo(
          planPrice,
          selectedMonthlyPrice ??
            getDiscountedPrice(planPrice, 0, apiData?.discountPercentage)
              .discountedPrice,
          offerDuration,
        );
  }, [
    apiData,
    isAnySubscribed,
    isComponentizationUser,
    isMultiFormulaSelectUser,
    currentSelectedSkusArray,
    allPlans,
    matchedPlanForSelected,
    planForNonSubscribed,
    isCustomizeMode,
    nonSubscribedProducts,
    unavailablePricing,
    offerDuration,
  ]);

  const pricingForUpgrade = useMemo(() => {
    if (
      isAnySubscribed &&
      nonSubscribedProducts.length > 0 &&
      planForNonSubscribed &&
      planForNonSubscribed.plan_product
    ) {
      const planPrice = parseFloat(planForNonSubscribed.plan_product.price);
      return getFormulaPriceInfo(
        planPrice,
        getDiscountedPrice(planPrice, 0, apiData?.discountPercentage)
          .discountedPrice,
        offerDuration,
      );
    }
    return unavailablePricing;
  }, [
    apiData,
    isAnySubscribed,
    nonSubscribedProducts,
    planForNonSubscribed,
    unavailablePricing,
    offerDuration,
  ]);

  const redirectToCheckoutViaMultipass = (url: string) => {
    // Safari/iOS only honor `window.open` when invoked synchronously inside a
    // user gesture. Opening here (not in the async `onSuccess`) preserves the
    // user-activation so the popup is not blocked; we then redirect the tab
    // once the multipass URL is available.
    const checkoutTab = window.open('', '_blank');

    generateMultipassUrl(url, {
      onSuccess: (multipassData) => {
        if (multipassData) {
          if (checkoutTab && !checkoutTab.closed) {
            checkoutTab.location.href = multipassData;
          } else {
            window.location.href = multipassData;
          }
        } else {
          if (checkoutTab && !checkoutTab.closed) {
            checkoutTab.close();
          }
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Could not retrieve checkout URL. Please try again.',
          });
          console.error(
            'Multipass URL generation succeeded but no URL was returned or data is invalid.',
            multipassData,
          );
        }
      },
      onError: (error: Error) => {
        if (checkoutTab && !checkoutTab.closed) {
          checkoutTab.close();
        }
        toast({
          variant: 'destructive',
          title: 'Error',
          description:
            error.message || 'Could not proceed to checkout. Please try again.',
        });
        console.error('Error during Shopify multipass process:', error);
      },
    });
  };

  const proceedToCheckoutWithPlan = (
    planToUse: typeof matchedPlanForSelected,
  ) => {
    const shopifyCartUrlFromPlan = planToUse?.plan_product?.shopify_cart_url;

    if (!shopifyCartUrlFromPlan) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Shopify cart URL not found for the current plan.',
      });
      console.error('Shopify cart URL not found for the current plan.');
      return;
    }

    let finalShopifyCartUrl = shopifyCartUrlFromPlan;
    const couponCodeValue = apiData?.couponCode || 'NOPROMO';

    const separator = finalShopifyCartUrl.includes('?') ? '&' : '?';
    finalShopifyCartUrl += `${separator}discount=${couponCodeValue}`;

    redirectToCheckoutViaMultipass(finalShopifyCartUrl);
  };

  const proceedToCheckoutWithMultipleSkus = (skus: string[]) => {
    const couponCodeValue = apiData?.couponCode || 'NOPROMO';

    const pairs = skus
      .map((sku) => {
        const product = products?.find((product) => product.sku === sku);
        const variantId = product?.variantId;
        const sellingPlanId = product?.sellingPlanId;
        return variantId && sellingPlanId
          ? `${variantId}:${sellingPlanId}`
          : null;
      })
      .filter((pair): pair is string => pair !== null);

    if (pairs.length !== skus.length) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Checkout plan not found for the selected formulas.',
      });
      console.error('Checkout plan not found for one or more selected SKUs.');
      return;
    }

    const checkoutUrl = `${ENV_VALUES.CHECKOUT_URL}/${pairs.join(',')}?discount=${couponCodeValue}`;
    redirectToCheckoutViaMultipass(checkoutUrl);
  };

  const handleCtaClick = () => {
    if (
      (isComponentizationUser || !isAnySubscribed) &&
      !dynamicPricing.isPriceAvailable
    ) {
      return;
    }
    // Componentization users (zero or existing subscriptions) always check
    // out directly through the multi-SKU checkout URL — the add-subscription
    // bulk API is legacy-only now.
    if (isComponentizationUser) {
      if (currentSelectedSkusArray.length > 0) {
        EventTracker.trackEvent(
          'shop',
          analyticsEvent.SHOP_MYFORMULAS_CONTINUETOORDERCTA,
        );
        proceedToCheckoutWithMultipleSkus(currentSelectedSkusArray);
      }
      return;
    }

    if (isAnySubscribed) {
      navigate(manageSubscriptionLink);
      return;
    }

    let planToUse;

    if (isCustomizeMode) {
      if (currentSelectedSkusArray.length > 0) {
        planToUse = matchedPlanForSelected;
      }
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_MYFORMULAS_CUSTOMIZE_CONTINUETOORDERCTA,
      );
    } else {
      if (currentSelectedSkusArray.length > 0) {
        planToUse = matchedPlanForSelected;
      } else {
        planToUse = planForNonSubscribed;
      }
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_MYFORMULAS_CONTINUETOORDERCTA,
      );
    }

    proceedToCheckoutWithPlan(planToUse);
  };

  if (isLoadingFormulas || isLoadingComponentizationStatus) {
    return <ShopFormulaSkeleton />;
  }

  if (isErrorFormulas) {
    return <ErrorLoadingPage errorType="error" hideNavBar hideBackground />;
  }

  if (!products || products.length === 0) {
    return <ShopUnavailablePage tab="formulas" />;
  }

  // Legacy users customize the bundle; checkbox buyers choose directly.
  const handleCustomizeClick = () => {
    setProductSelection(
      selectedProductIds.size ? selectedProductIds : allSelectableProductIds,
    );
    setIsCustomizeMode(true);
    EventTracker.trackEvent(
      'shop',
      analyticsEvent.SHOP_MYFORMULAS_CUSTOMIZE_BUTTON,
    );
  };

  const handleBackClick = () => {
    setIsCustomizeMode(false);
  };

  const handleToggleProductSelected = (productId: string) => {
    const nextSelection = new Set(selectedProductIds);
    if (nextSelection.has(productId)) {
      nextSelection.delete(productId);
    } else {
      nextSelection.add(productId);
    }
    setProductSelection(nextSelection);
  };

  const handleSelectSingleProduct = (productId: string) => {
    setProductSelection(new Set([productId]));
  };

  const handleSelectAll = () => {
    setProductSelection(allSelectableProductIds);
    EventTracker.trackEvent(
      'shop',
      analyticsEvent.SHOP_MYFORMULAS_CUSTOMIZE_SELECTALL,
    );
  };

  const ctaButtonLabel =
    isAnySubscribed && isComponentizationUser
      ? 'Continue to Order'
      : isAnySubscribed
        ? 'Change My Plan'
        : 'Continue to Order';

  if (isCustomizeMode) {
    return (
      <CustomizeModeView
        handleBackClick={handleBackClick}
        nonSubscribedProducts={nonSubscribedProducts}
        selectedProductIds={selectedProductIds}
        handleToggleProductSelected={handleToggleProductSelected}
        handleSelectAll={handleSelectAll}
        dynamicPricing={dynamicPricing}
        ctaButtonLabel={ctaButtonLabel}
        onCtaClick={handleCtaClick}
        isCtaLoading={isMultipassLoading}
      />
    );
  }

  if (isAllSubscribed) {
    return <AllSubscribedView products={products} />;
  } else if (isAnySubscribed) {
    return (
      <AnySubscribedView
        selectedFormulaCount={currentSelectedSkusArray.length}
        pricingForUpgrade={pricingForUpgrade}
        ctaButtonLabel={ctaButtonLabel}
        nonSubscribedProducts={nonSubscribedProducts}
        products={products}
        onCtaClick={handleCtaClick}
        isCtaLoading={isMultipassLoading}
        isComponentizationUser={isComponentizationUser}
        isMultiFormulaSelectUser={isMultiFormulaSelectUser}
        selectedProductIds={selectedProductIds}
        handleSelectSingleProduct={handleSelectSingleProduct}
        handleToggleProductSelected={handleToggleProductSelected}
        dynamicPricing={dynamicPricing}
        discountPercentage={apiData?.discountPercentage}
      />
    );
  } else {
    return (
      <NotSubscribedView
        selectedFormulaCount={currentSelectedSkusArray.length}
        promotionData={promo}
        dynamicPricing={dynamicPricing}
        ctaButtonLabel={ctaButtonLabel}
        selectedProductIds={selectedProductIds}
        nonSubscribedProducts={nonSubscribedProducts}
        products={products}
        handleCustomizeClick={handleCustomizeClick}
        isAnySubscribed={isAnySubscribed}
        onCtaClick={handleCtaClick}
        isCtaLoading={isMultipassLoading}
        isComponentizationUser={isComponentizationUser}
        handleToggleProductSelected={handleToggleProductSelected}
        discountPercentage={apiData?.discountPercentage}
      />
    );
  }
};
